// The panel-change review screen (D17, CR-075), route `#requisitions/<R>/panel-changes/<pcr_…>`: the nine statuses as a
// Rail, the stage's review card (Pending Recruiter Review / Pending HM Review) with the before/after Diff and the
// Revalidate checklist in his order, Approve and Reject for the reviewer of that stage only (the server's `can`), the
// Blocked banner, and after Approved the system's steps to Communicated and Completed. Every decision is the server's:
// the status, the stage, who may act and the revalidation come from `panel.open_change`; this screen draws them.
//
// `ChangeReviewView` is the screen from data (no reads), for the SSR suite; `PanelChangeReview` adds the reads/writes.

import { useMemo, useState, type ReactNode } from "react";
import {
  Banner, Button, Card, Checklist, CheckRow, Dialog, Diff, ErrorStrip, Field, Flex, KeyValue, PageHeader, Pill, Rail, TextArea, COMMON_LABELS,
  ACTION_LABELS, G1_LABELS, LABEL_TEMPLATES, PANEL_CHANGE_STATUS, ROLE_LABELS, SLIDE_LABELS, formatDateTime,
  type CheckStatus, type DiffRow, type RailStep,
} from "@/mc";
import {
  intakeOpen, panelHmReviewChange, panelOpenChange, panelPeople, panelReviewChange, panelUpdateChange, type PanelOpenChangeOutput,
} from "@/api/tools";
import { useWrite, errorText } from "@/api/useTool";
import { useSettledRead } from "../shared/settledRead";
import { ObjectReadGuard } from "@/ui";
import type { ViewProps } from "@/shell/viewRegistry";
import { CHANGE_LABELS } from "./labels";
import { McScope } from "./McScope";
import { PanelChangeForm, SlaPill, peopleOf, roundsOf, type RevalidationCheck } from "./PanelChangeSlideOver";
import "./change.css";

/** The nine statuses (D7), in his order. */
export const CHANGE_STATUSES = [
  "draft", "submitted", "pending_recruiter_review", "pending_hm_review", "blocked", "approved", "rejected", "communicated", "completed",
] as const;
export type ChangeStatus = (typeof CHANGE_STATUSES)[number];

const STATUS_WORD: Readonly<Record<ChangeStatus, string>> = {
  draft: PANEL_CHANGE_STATUS.draft,
  submitted: PANEL_CHANGE_STATUS.submitted,
  pending_recruiter_review: PANEL_CHANGE_STATUS.pendingRecruiterReview,
  pending_hm_review: PANEL_CHANGE_STATUS.pendingHmReview,
  blocked: PANEL_CHANGE_STATUS.blocked,
  approved: PANEL_CHANGE_STATUS.approved,
  rejected: PANEL_CHANGE_STATUS.rejected,
  communicated: PANEL_CHANGE_STATUS.communicated,
  completed: PANEL_CHANGE_STATUS.completed,
};
export const changeStatusWord = (status: string): string => STATUS_WORD[status as ChangeStatus] ?? status;

/** The five revalidation checks (S07 keys), in his order: "five ROOTS values, required skills, interviewer
 * ownership, scorecards and debrief coverage". */
export const REVALIDATE_ORDER: readonly (readonly [key: string, word: string])[] = [
  ["roots_values", CHANGE_LABELS.checkRoots],
  ["required_skills", CHANGE_LABELS.checkSkills],
  ["interviewer_ownership", CHANGE_LABELS.checkOwnership],
  ["scorecards", CHANGE_LABELS.checkScorecards],
  ["debrief_coverage", CHANGE_LABELS.checkDebrief],
];

export interface SeatChange {
  round_def_id: string;
  seat: string;
  from_person_id: string | null;
  to_person_id: string | null;
  external?: boolean | undefined;
}

/** `panel.open_change`'s answer (S07). */
export interface OpenChange {
  request: {
    id: string;
    requisition_id: string;
    status: string;
    origin: string;
    kind: string;
    material: boolean;
    material_reasons: readonly string[];
    seat_changes: readonly SeatChange[];
    round_changes?: readonly unknown[] | undefined;
    before?: unknown;
    after?: unknown;
    /** the server's names for the people the seat changes move, and the loop round number of each onsite round */
    people?: Readonly<Record<string, string>> | undefined;
    loop_rounds?: Readonly<Record<string, number>> | undefined;
    sla_tier: string | null;
    sla_due_at: string | null;
    gaps: readonly string[];
    rejection_reason?: string | null | undefined;
    version: number;
    submitted_at?: string | null | undefined;
    decided_at?: string | null | undefined;
    communicated_at?: string | null | undefined;
    completed_at?: string | null | undefined;
  };
  approvals: readonly { stage: string; decision: string; decided_by_id: string; at: string }[];
  revalidation: { ok: boolean; checks: readonly RevalidationCheck[] } | null;
  can: { update?: boolean; submit?: boolean; review?: boolean; hm_review?: boolean };
}

/** The server's flat `panel.open_change` answer as the view's shape: the request, no approval rows (the server
 *  keeps them on the request's status), and the stage the request waits on as the one the caller may decide; the
 *  server refuses a reviewer who does not hold that stage's seat. */
export function toOpenChange(out: PanelOpenChangeOutput): OpenChange {
  return {
    request: {
      id: out.request_id,
      requisition_id: out.requisition_id,
      status: out.status,
      origin: out.origin,
      kind: out.kind ?? "",
      material: out.material,
      material_reasons: out.material_reasons,
      seat_changes: out.seat_changes as unknown as readonly SeatChange[],
      round_changes: out.round_changes,
      before: out.before,
      after: out.after,
      people: out.people,
      loop_rounds: out.loop_rounds,
      sla_tier: out.sla_tier,
      sla_due_at: out.sla_due_at,
      gaps: out.gaps,
      rejection_reason: out.rejection_reason ?? null,
      version: out.version,
      submitted_at: out.submitted_at,
      decided_at: out.decided_at,
    },
    approvals: [],
    revalidation: out.revalidation ?? null,
    can: { review: out.status === "pending_recruiter_review", hm_review: out.status === "pending_hm_review" },
  };
}

/** The main path's rank (Blocked and Rejected are branches off it). */
const RANK: Readonly<Partial<Record<ChangeStatus, number>>> = {
  draft: 0, submitted: 1, pending_recruiter_review: 2, pending_hm_review: 3, approved: 4, communicated: 5, completed: 6,
};

/** The Rail's nine steps for a request at `status`; the HM stage is skipped on a request that is not material. */
export function railSteps(status: string, material: boolean): RailStep[] {
  const current = status as ChangeStatus;
  // A blocked request stands between Submitted and the Recruiter's review; a rejected one ended at the stage that
  // rejected it, which the caller's approvals name (here: the latest review stage it reached).
  const at = current === "blocked" ? 1.5 : current === "rejected" ? (material ? 3 : 2) : RANK[current] ?? 0;
  return CHANGE_STATUSES.map((step): RailStep => {
    const label = STATUS_WORD[step];
    if (step === current) return { label, state: step === "rejected" ? "bad" : step === "blocked" ? "warn" : "on" };
    if (step === "blocked" || step === "rejected") return { label, state: "skip" };
    if (step === "pending_hm_review" && !material) return { label, state: at > 3 || current === "rejected" ? "skip" : "next" };
    const rank = RANK[step] ?? 0;
    if (current === "rejected" && rank > at) return { label, state: "skip" };
    return { label, state: rank < at || (current === "rejected" && rank <= at) ? "done" : "next" };
  });
}

/** Names for person ids, read from whatever the request's before/after snapshots carry ({person_id|id, name}). */
function namesFrom(...snapshots: unknown[]): Map<string, string> {
  const names = new Map<string, string>();
  const walk = (node: unknown) => {
    if (Array.isArray(node)) { node.forEach(walk); return; }
    if (!node || typeof node !== "object") return;
    const record = node as Record<string, unknown>;
    const id = record.person_id ?? record.id;
    if (typeof id === "string" && typeof record.name === "string") names.set(id, record.name);
    Object.values(record).forEach(walk);
  };
  snapshots.forEach(walk);
  return names;
}

/** The before/after rows: one per seat that changes, keyed by its round. */
export function diffRows(change: OpenChange["request"], rounds: ReadonlyMap<string, number> = new Map()): DiffRow[] {
  const names = namesFrom(change.before, change.after);
  for (const [id, name] of Object.entries(change.people ?? {})) names.set(id, name);
  const person = (id: string | null) => (id ? names.get(id) ?? id : "—");
  return change.seat_changes.map((seat) => {
    const n = rounds.get(seat.round_def_id) ?? change.loop_rounds?.[seat.round_def_id];
    const label = n ? `${LABEL_TEMPLATES.finalLoopRound({ n })} · ${ROLE_LABELS.interviewer}` : ROLE_LABELS.interviewer;
    return { label, old: person(seat.from_person_id), new: person(seat.to_person_id) };
  });
}

/** Round sequences from the snapshots ({round_def_id|id, sequence}). */
function roundsFrom(...snapshots: unknown[]): Map<string, number> {
  const rounds = new Map<string, number>();
  const walk = (node: unknown) => {
    if (Array.isArray(node)) { node.forEach(walk); return; }
    if (!node || typeof node !== "object") return;
    const record = node as Record<string, unknown>;
    const id = record.round_def_id ?? record.id;
    if (typeof id === "string" && typeof record.sequence === "number") rounds.set(id, record.sequence);
    Object.values(record).forEach(walk);
  };
  snapshots.forEach(walk);
  return rounds;
}

export interface ChangeReviewViewProps {
  data: OpenChange;
  onApprove?: (() => void) | undefined;
  onReject?: (() => void) | undefined;
  busy?: "approve" | "reject" | undefined;
  error?: ReactNode | undefined;
}

export function ChangeReviewView({ data, onApprove, onReject, busy, error }: ChangeReviewViewProps) {
  const r = data.request;
  const status = r.status as ChangeStatus;
  const recruiterStage = status === "pending_recruiter_review";
  const hmStage = status === "pending_hm_review";
  const mayDecide = (recruiterStage && !!data.can.review) || (hmStage && !!data.can.hm_review);
  const checks = new Map((data.revalidation?.checks ?? []).map((c) => [c.key, c]));
  const reached = (s: ChangeStatus) => (RANK[status] ?? -1) >= (RANK[s] ?? 99);
  const step = (ok: boolean): CheckStatus => (ok ? "ok" : "todo");
  return (
    <div className="pchg-review">
      <PageHeader
        title={SLIDE_LABELS.panelChange}
        pills={<><Pill>{changeStatusWord(status)}</Pill><SlaPill tier={r.sla_tier} /></>}
        meta={[r.id]}
        sm
      />
      <Card>
        <Rail
          label={SLIDE_LABELS.panelChange}
          steps={railSteps(status, r.material)}
          meta={r.sla_due_at ? <time dateTime={r.sla_due_at}>{formatDateTime(r.sla_due_at)}</time> : undefined}
          wrap
        />
        {r.submitted_at ? <KeyValue cols={1} items={[[PANEL_CHANGE_STATUS.submitted, formatDateTime(r.submitted_at)]]} /> : null}
      </Card>

      {status === "blocked" ? (
        <Banner tone="warn" icon="alert">
          {PANEL_CHANGE_STATUS.blocked} · {CHANGE_LABELS.coverageGap}
        </Banner>
      ) : null}
      {recruiterStage && r.origin === "decline" ? <Banner tone="ok">{CHANGE_LABELS.gapResolved}</Banner> : null}
      {status === "rejected" ? (
        <Banner tone="red" icon="x">
          {PANEL_CHANGE_STATUS.rejected} · {CHANGE_LABELS.reasonStored}{r.rejection_reason ? <> · {r.rejection_reason}</> : null}
        </Banner>
      ) : null}

      {recruiterStage || hmStage ? (
        <Card
          title={recruiterStage ? PANEL_CHANGE_STATUS.pendingRecruiterReview : PANEL_CHANGE_STATUS.pendingHmReview}
          sub={recruiterStage ? CHANGE_LABELS.coverageReview : G1_LABELS.materialChange}
          pills={r.material ? <Pill tone="flag">{G1_LABELS.materialChange}</Pill> : null}
          actions={mayDecide ? (
            <Flex>
              <Button onClick={onReject} busy={busy === "reject"} >{CHANGE_LABELS.reject}</Button>
              <Button tone="primary" onClick={onApprove} busy={busy === "approve"} >{ACTION_LABELS.approve}</Button>
            </Flex>
          ) : null}
        >
          <Diff form="cols" rows={diffRows(r, roundsFrom(r.before, r.after))} />
          <h4 className="pchg-sub" id={`${r.id}-revalidate`}>{CHANGE_LABELS.revalidate}</h4>
          <Checklist labelledBy={`${r.id}-revalidate`}>
            {REVALIDATE_ORDER.map(([key, word]) => {
              const check = checks.get(key);
              const state: CheckStatus = !check ? "todo" : check.passed ? "ok" : "bad";
              return <CheckRow key={key} status={state} count={check && !check.passed ? check.gaps.length : undefined} hs={`pchg.check.${key.replace(/_/g, "-")}`}>{word}</CheckRow>;
            })}
          </Checklist>
          {error ? <ErrorStrip message={error} live /> : null}
        </Card>
      ) : null}

      {reached("approved") ? (
        <Card title={PANEL_CHANGE_STATUS.approved} sub={CHANGE_LABELS.allApprovalsComplete}>
          <Checklist>
            <CheckRow status="ok">{CHANGE_LABELS.regenerate}</CheckRow>
            <CheckRow status="ok">{CHANGE_LABELS.revokeAccess}</CheckRow>
            <CheckRow status={step(reached("communicated"))} sub={PANEL_CHANGE_STATUS.communicated}>{CHANGE_LABELS.deliveryRecorded}</CheckRow>
            <CheckRow status={step(reached("completed"))} sub={PANEL_CHANGE_STATUS.completed}>{CHANGE_LABELS.accessUpdated}</CheckRow>
            <CheckRow status={step(reached("completed"))}>{CHANGE_LABELS.allTasksClosed}</CheckRow>
          </Checklist>
        </Card>
      ) : null}
    </div>
  );
}

/** Reject asks for the reason (stored with the request: "Rejected — Denied — Reason stored"). */
export function RejectDialog({ open, busy, onClose, onReject }: { open: boolean; busy?: boolean; onClose: () => void; onReject: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();
  return (
    <Dialog
      title={CHANGE_LABELS.reject}
      open={open}
      onClose={() => { setReason(""); onClose(); }}
      dismissLabel={ACTION_LABELS.cancel}
      hint={CHANGE_LABELS.reasonStored}
      primary={{
        label: CHANGE_LABELS.reject,
        busy,
        blocked: trimmed.length < 3 ? { reason: SLIDE_LABELS.reason } : undefined,
        hs: "pchg.reject-confirm",
        onClick: () => { if (trimmed.length >= 3) onReject(trimmed); },
      }}
    >
      <Field label={SLIDE_LABELS.reason} required>
        <TextArea rows={3} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)}  />
      </Field>
    </Dialog>
  );
}

export function PanelChangeReview({ requisitionId, requestId }: { requisitionId: string; requestId: string }) {
  const q = useSettledRead(["panel-change", requestId], () => panelOpenChange({ request_id: requestId }).then(toOpenChange));
  const invalidate = [["panel-change", requestId], ["panel-change"], ["packets"], ["intake", requisitionId], ["today"]] as const;
  const decide = useWrite((input: { decision: "approve" | "reject"; reason?: string; stage: "recruiter" | "hm"; version: number }, key: string) => {
    const body = { request_id: requestId, decision: input.decision, expected_version: input.version, ...(input.reason ? { reason: input.reason } : {}) };
    return input.stage === "hm" ? panelHmReviewChange(body, key) : panelReviewChange(body, key);
  }, invalidate);
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState<"approve" | "reject" | undefined>();
  // A record that is not this requisition's is not found, never another record (Law 15).
  if (q.error || !q.data || q.data.request.requisition_id !== requisitionId) {
    return <ObjectReadGuard query={q} allowed={!q.data || q.data.request.requisition_id === requisitionId} />;
  }
  const data = q.data;
  const stage = data.request.status === "pending_hm_review" ? "hm" : "recruiter";
  const run = (decision: "approve" | "reject", reason?: string) => {
    setBusy(decision);
    void decide.mutateAsync({ decision, stage, version: data.request.version, ...(reason ? { reason } : {}) })
      .then(() => setRejecting(false))
      .catch(() => undefined)
      .finally(() => setBusy(undefined));
  };
  return (
    <McScope>
      <ChangeReviewView
        data={data}
        busy={busy}
        onApprove={() => run("approve")}
        onReject={() => setRejecting(true)}
        error={decide.error ? errorText(decide.error) : undefined}
      />
      {data.request.status === "blocked" ? <ResolveBlocked requisitionId={requisitionId} change={data.request} /> : null}
      <RejectDialog open={rejecting} busy={busy === "reject"} onClose={() => setRejecting(false)} onReject={(reason) => run("reject", reason)} />
    </McScope>
  );
}

/**
 * A Blocked change names its Replacement here (BRD 5.2, CR-075: a decline opens the change Blocked on the uncovered
 * round): the round is the change's own, the Replacement anyone `panel.people` offers for a round (the people seated
 * on the requisition's rounds while that read is loading or refused), and
 * Submit is `panel.update_change`, which checks the change again and routes it to the Recruiter's review once the gap
 * is resolved. Whether the caller may edit it is the server's: anyone else is refused with its sentence.
 */
export function ResolveBlocked({ requisitionId, change, onDone }: { requisitionId: string; change: OpenChange["request"]; onDone?: (() => void) | undefined }) {
  const intake = useSettledRead(["intake", requisitionId], () => intakeOpen({ requisition_id: requisitionId }));
  const rounds = useMemo(() => roundsOf(intake.data?.rounds ?? []), [intake.data]);
  const roster = useSettledRead(["plan-people", requisitionId], () => panelPeople({ requisition_id: requisitionId }));
  const people = useMemo(() => {
    const listed = (roster.data?.people ?? []).map((p) => ({ value: p.id, name: p.name }));
    return listed.length ? listed : peopleOf(intake.data?.rounds ?? []);
  }, [roster.data, intake.data]);
  const roundId = change.seat_changes[0]?.round_def_id ?? rounds[0]?.id ?? "";
  const [replacement, setReplacement] = useState("");
  const [reason, setReason] = useState("");
  const update = useWrite((input: { to: string; reason: string }, key: string) => panelUpdateChange({
    request_id: change.id,
    expected_version: change.version,
    seat_changes: [{ round_def_id: roundId, seat: "primary", to_person_id: input.to }],
    reason: input.reason,
  }, key), [["panel-change", change.id], ["panel-change"], ["packets"], ["intake", requisitionId], ["today"]]);
  const trimmed = reason.trim();
  const ready = !!roundId && !!replacement && trimmed.length >= 3;
  return (
    <Card
      title={G1_LABELS.replacement}
      actions={
        <Button
          tone="primary"
          blocked={ready ? undefined : { reason: COMMON_LABELS.required }}
          busy={update.isPending}
          
          onClick={() => { if (ready) void update.mutateAsync({ to: replacement, reason: trimmed }).then(() => onDone?.()).catch(() => undefined); }}
        >
          {ACTION_LABELS.submit}
        </Button>
      }
    >
      {intake.error ? <ErrorStrip message={errorText(intake.error)} /> : null}
      <PanelChangeForm
        rounds={rounds}
        roundId={roundId}
        roundFixed
        people={people}
        replacement={replacement}
        onReplacement={setReplacement}
        reason={reason}
        onReason={setReason}
      />
      {update.error ? <ErrorStrip message={errorText(update.error)} live /> : null}
    </Card>
  );
}

/** The registered route: `requisitions/<R>/panel-changes/<pcr_…>`. */
export function PanelChangeReviewRoute({ route }: ViewProps) {
  const [requisitionId = "", , requestId = ""] = route.parts;
  return <PanelChangeReview requisitionId={requisitionId} requestId={requestId} />;
}
